import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp, ChevronUp, ChevronsUp } from 'lucide-react';

const ScrollToTop = ({
                         showAfter = 300,
                         smooth = true,
                         variant = 'default',
                         position = 'bottom-right',
                         showProgress = false
                     }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const {pathname} = useLocation();

    // Reset scroll position on every route change - without this, navigating
    // to a new page keeps whatever scroll offset the previous page was at.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        const toggleVisibility = () => {
            const scrollTop = window.pageYOffset;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

            setIsVisible(scrollTop > showAfter);
            setScrollProgress(scrollPercent);
        };

        const throttledToggleVisibility = throttle(toggleVisibility, 100);
        window.addEventListener('scroll', throttledToggleVisibility);

        return () => window.removeEventListener('scroll', throttledToggleVisibility);
    }, [showAfter]);

    const scrollToTop = () => {
        if (smooth) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        } else {
            window.scrollTo(0, 0);
        }
    };

    // Throttle function to optimize scroll performance
    const throttle = (func, limit) => {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    };

    const positionClasses = {
        'bottom-right': 'bottom-6 right-6',
        'bottom-left': 'bottom-6 left-6',
        'bottom-center': 'bottom-6 left-1/2 transform -translate-x-1/2',
        'top-right': 'top-20 right-6',
        'top-left': 'top-20 left-6'
    };

    const variants = {
        default: {
            button: 'bg-highland-500 hover:bg-highland-600 text-white shadow-lg hover:shadow-xl',
            size: 'w-12 h-12'
        },
        minimal: {
            button: 'bg-white hover:bg-soil-100 text-soil-600 shadow-md border border-soil-100 hover:border-soil-100',
            size: 'w-10 h-10'
        },
        large: {
            button: 'bg-highland-500 hover:bg-highland-600 text-white shadow-xl hover:shadow-2xl',
            size: 'w-14 h-14'
        },
        dark: {
            button: 'bg-soil-900 hover:bg-soil-900 text-white shadow-lg hover:shadow-xl',
            size: 'w-12 h-12'
        },
        gradient: {
            button: 'bg-highland-500 hover:bg-highland-600 text-white shadow-lg hover:shadow-xl',
            size: 'w-12 h-12'
        }
    };

    if (!isVisible) return null;

    // Progress circle variant
    if (variant === 'progress' || showProgress) {
        return (
            <div className={`fixed ${positionClasses[position]} z-50`}>
                <button
                    onClick={scrollToTop}
                    className="relative w-12 h-12 bg-white hover:bg-soil-50 text-highland-500 rounded-full shadow-lg border border-soil-100 flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-highland-500 focus:ring-offset-2"
                    aria-label="Scroll to top"
                >
                    {/* Progress ring background */}
                    <svg className="absolute inset-0 w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeOpacity="0.1"
                            className="text-soil-100"
                        />
                        <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeDasharray={`${scrollProgress}, 100`}
                            className="text-highland-500 transition-all duration-150"
                        />
                    </svg>
                    <ChevronUp className="w-5 h-5 relative z-10" />
                </button>
            </div>
        );
    }

    // Standard variants
    return (
        <div className={`fixed ${positionClasses[position]} z-50`}>
            <button
                onClick={scrollToTop}
                className={`${variants[variant].button} ${variants[variant].size} rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-highland-500 focus:ring-offset-2 group`}
                aria-label="Scroll to top"
            >
                <ArrowUp className="w-5 h-5 group-hover:animate-bounce" />
            </button>
        </div>
    );
};

// Enhanced ScrollToTop with tooltip and multiple features
export const EnhancedScrollToTop = ({
                                        showAfter = 300,
                                        showTooltip = true,
                                        tooltipText = "Back to top"
                                    }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [showTooltipState, setShowTooltipState] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

            setIsVisible(scrollTop > showAfter);
            setScrollProgress(scrollPercent);
        };

        const throttledHandleScroll = throttle(handleScroll, 100);
        window.addEventListener('scroll', throttledHandleScroll);

        return () => window.removeEventListener('scroll', throttledHandleScroll);
    }, [showAfter]);

    const throttle = (func, limit) => {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50">
            {/* Tooltip */}
            {showTooltip && showTooltipState && (
                <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-soil-900 text-white text-sm rounded-md whitespace-nowrap">
                    {tooltipText}
                    <div className="absolute top-full right-4 border-l-4 border-r-4 border-t-4 border-transparent border-t-soil-900"></div>
                </div>
            )}

            {/* Button with progress indicator */}
            <button
                onClick={scrollToTop}
                onMouseEnter={() => setShowTooltipState(true)}
                onMouseLeave={() => setShowTooltipState(false)}
                className="relative w-12 h-12 bg-white hover:bg-soil-50 text-highland-500 rounded-full shadow-lg border border-soil-100 flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-highland-500 focus:ring-offset-2 group"
                aria-label={tooltipText}
            >
                {/* Progress ring */}
                <svg className="absolute inset-0 w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeOpacity="0.1"
                        className="text-soil-100"
                    />
                    <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeDasharray={`${scrollProgress}, 100`}
                        className="text-highland-500 transition-all duration-150"
                    />
                </svg>

                {/* Icon */}
                <ChevronUp className="w-5 h-5 relative z-10 group-hover:animate-bounce" />
            </button>
        </div>
    );
};

// Animated ScrollToTop with multiple scroll states
export const AnimatedScrollToTop = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollState, setScrollState] = useState('hidden'); // hidden, visible, near-top

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const windowHeight = window.innerHeight;

            if (scrollTop < 300) {
                setScrollState('hidden');
                setIsVisible(false);
            } else if (scrollTop < windowHeight) {
                setScrollState('visible');
                setIsVisible(true);
            } else {
                setScrollState('near-top');
                setIsVisible(true);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-6 right-6 z-50">
            <button
                onClick={scrollToTop}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-highland-500 focus:ring-offset-2 ${
                    scrollState === 'near-top'
                        ? 'bg-highland-500 text-white shadow-xl animate-pulse'
                        : 'bg-highland-500 hover:bg-highland-600 text-white shadow-lg hover:shadow-xl'
                }`}
                aria-label="Scroll to top"
            >
                {scrollState === 'near-top' ? (
                    <ChevronsUp className="w-6 h-6 animate-bounce" />
                ) : (
                    <ChevronUp className="w-5 h-5" />
                )}
            </button>
        </div>
    );
};

// Compact ScrollToTop for mobile
export const CompactScrollToTop = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const toggleVisibility = () => {
            setIsVisible(window.pageYOffset > 300);
        };

        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    if (!isVisible) return null;

    return (
        <button
            onClick={scrollToTop}
            className="fixed bottom-4 right-4 z-50 w-10 h-10 bg-highland-500 hover:bg-highland-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-highland-500 focus:ring-offset-2"
            aria-label="Scroll to top"
        >
            <ArrowUp className="w-4 h-4" />
        </button>
    );
};

export default ScrollToTop;